/**
 * PostHog Scroll Depth Tracker
 * ---------------------------
 * 
 * This script tracks how far users scroll down pages, firing events at key thresholds.
 * 
 * EVENT FIRED:
 *   - scroll_depth_reached: When user reaches 25%, 50%, 75%, 90%, or 100% of page
 *     Properties: depth, page_path, page_url, page_title, ab_variant_percentage (if available), campaign_id (if available)
 * 
 * IMPLEMENTATION NOTES:
 *   - Uses throttling (500ms) to limit scroll event processing
 *   - Automatically integrates with A/B testing variables
 *   - Handles window resizing and dynamic content changes
 */

(() => {
  "use strict";
  
  // Configuration
  const scrollDepthThresholds = [25, 50, 75, 90, 100];
  let maxScrollDepthReached = 0;
  const reachedThresholds = new Set();
  
  // Utility function
  function getScrollDepth() {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) return 100;
    const scrolled = window.scrollY;
    return Math.min(100, Math.round((scrolled / scrollHeight) * 100));
  }
  
  // Throttle function to limit scroll event processing
  function throttle(func, delay) {
    let lastCall = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        return func.apply(this, args);
      }
    };
  }
  
  // Handle scroll events
  function handleScroll() {
    try {
      const currentDepth = getScrollDepth();

      // Update max scroll depth
      if (currentDepth > maxScrollDepthReached) {
        maxScrollDepthReached = currentDepth;
        
        // Check if we hit a threshold
        for (const threshold of scrollDepthThresholds) {
          if (maxScrollDepthReached >= threshold && !reachedThresholds.has(threshold) && window.posthog) {
            reachedThresholds.add(threshold);
            
            const eventProps = {
              depth: threshold,
              page_path: window.location.pathname,
              page_url: window.location.href,
              page_title: document.title
            };

            // Add A/B test context if available
            if (window.INITIAL_AB_VARIANT !== undefined) {
              eventProps.ab_variant_percentage = window.INITIAL_AB_VARIANT;
            }
            const campaignMatch = document.cookie.match(/campaign_id=([^;]+)/);
            if (campaignMatch && campaignMatch[1]) {
              eventProps.campaign_id = campaignMatch[1];
            }

            window.posthog.capture('scroll_depth_reached', eventProps);
          }
        }
      }
    } catch (error) {
      // Silently log errors without breaking the page
      console.error('[PostHog Scroll Tracker] Error:', error);
    }
  }
  
  // Initialize
  function init(retries = 0) {
    const MAX_RETRIES = 10;
    
    if (typeof window.posthog === 'undefined') {
      if (retries < MAX_RETRIES) {
        setTimeout(() => init(retries + 1), 1000);
      } else {
        console.warn('[PostHog Scroll Tracker] PostHog not available after maximum retries');
      }
      return;
    }
    
    window.addEventListener('scroll', throttle(handleScroll, 500));
    handleScroll();
  }
  
  // Start tracking when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
